import React, { useState } from "react";
import { Bar } from "react-chartjs-2";

const GLOverallAssessment = ({
    lang,
    child,
    responses,
    deepDiveResponses,
    domainStats,
    deepDiveStats,
    consistencyIndex,
    deepDiveInsightIndex,
    doctorSummary,
    buildChartData,
    darkChartOptions,
    handlePrint,
    handleDownloadPDF,
    navigate,
    allIssues,
    getIssueStats,
    selectedIssueId,
    setSelectedIssueId,
    categoryLabels,
    selectedCategory,
    setSelectedCategory,
    categoryMapping,
    selectedRole,
    setSelectedRole,
    hasAnyResponseAll,
    SCALE,
    buildOverallChartData,
    updateScore,
    issueConsistencyIndex,
    bandColor,
}) => {
    const currentIssue = allIssues.find((i) => i.id === selectedIssueId);

    function EmptyState({ message }) {
      return (
        <div className="flex flex-col items-center justify-center py-10 text-center text-gray-100">
          <i className="fa-solid fa-chart-simple text-lg lg:text-2xl mb-3 opacity-30" />
          <p className="text-xs text-gray-100 leading-relaxed max-w-xs">
            {message}
          </p>
        </div>
      );
    }

    return (
      <div className="flex justify-center py-2">
        <div className="w-full max-w-2xl space-y-4 text-white">
          <div className="text-center">
            <h2 className="text-base font-bold text-cyan-300 uppercase tracking-wide">
              {lang === "hi" ? "समग्र मूल्यांकन" : "Overall Assessment"}
            </h2>
            <p className="text-xs text-gray-100 mt-0.5">
              {lang === "hi"
                ? "श्रेणी और मुद्दा चुनें, फिर पर्यवेक्षक चुनें"
                : "Choose a category and issue, then select an observer role"}
            </p>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 gap-2">
            {/* Category tabs */}
            <div className="flex rounded-lg overflow-hidden border border-gray-700">
              {Object.entries(categoryLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { setSelectedCategory(key); setSelectedIssueId(null); }}
                  className={`flex-1 py-1.5 text-xs font-semibold transition ${
                    selectedCategory === key
                      ? "bg-cyan-600 text-white"
                      : "bg-gray-900 text-gray-100 hover:bg-gray-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Issue + Observer row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-100 mb-1 uppercase tracking-wider">
                  {lang === "hi" ? "मुद्दा" : "Issue"}
                </label>
                <select
                  value={selectedIssueId || ""}
                  onChange={(e) => setSelectedIssueId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1 text-xs text-white"
                >
                  <option value="">
                    {lang === "hi" ? "— चुनें —" : "— Select —"}
                  </option>
                  {categoryMapping[selectedCategory].map((issue) => (
                    <option key={issue.id} value={issue.id}>
                      {issue.title[lang]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-100 mb-1 uppercase tracking-wider">
                  {lang === "hi" ? "भूमिका" : "Role"}
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="bg-gray-900 border border-gray-700 rounded-lg w-fit px-2 py-1 text-xs text-white"
                >
                  <option value="parent">{lang === "hi" ? "अभिभावक" : "Parent"}</option>
                  <option value="educator">{lang === "hi" ? "शिक्षक"  : "Educator"}</option>
                  <option value="doctor">{lang === "hi" ? "डॉक्टर"  : "Doctor"}</option>
                  <option value="observer">{lang === "hi" ? "अवलोकक" : "Observer"}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Overview chart */}
          <div className="bg-gray-950 border border-gray-800 rounded-xl p-3">
            <p className="text-xs text-gray-100 mb-2 uppercase tracking-wider">
              {lang === "hi" ? "सभी मुद्दों का अवलोकन" : "Score overview — all issues"}
            </p>
            {!hasAnyResponseAll() ? (
              <EmptyState
                message={
                  lang === "hi"
                    ? "किसी मुद्दे पर उत्तर देने के बाद यहाँ चार्ट दिखेगा।"
                    : "Answer any issue's questions to see the overview chart."
                }
              />
            ) : (
              <div style={{ height: 200 }}>
                <Bar data={buildOverallChartData()} options={darkChartOptions()} />
              </div>
            )}
          </div>

          {/* Doctor Summary — cross-role consistency pattern, non-diagnostic */}
          {hasAnyResponseAll() && doctorSummary && (
            <div className="bg-gray-950 border border-gray-800 rounded-xl p-3">
              <p className="text-xs text-gray-100 mb-2 uppercase tracking-wider">
                {lang === "hi" ? "डॉक्टर सारांश" : "Doctor Summary"}
              </p>
              <p className="text-xs text-gray-50 leading-relaxed">
                {doctorSummary}
              </p>
            </div>
          )}

          {/* Empty issue state */}
          {!selectedIssueId && (
            <div className="border border-dashed border-gray-700 rounded-xl p-6 text-center text-gray-200 text-xs">
              {lang === "hi"
                ? "ऊपर से एक मुद्दा चुनें"
                : "Select an issue above to begin the assessment"}
            </div>
          )}

          {/* Issue detail */}
          {selectedIssueId && currentIssue && (
            <div className="border border-gray-800 rounded-xl p-4 bg-gray-950 space-y-3">
              <h3 className="text-sm font-bold text-cyan-300">
                {currentIssue.title[lang]}
              </h3>

              {/* Questions */}
              {currentIssue.questions[selectedRole]?.[lang]?.map((q, qi) => (
                <div key={qi}>
                  <p className="text-xs text-white mb-1.5 leading-relaxed">
                    {qi + 1}. {q}
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {SCALE.map((v) => (
                      <button
                        key={v}
                        onClick={() => updateScore(selectedRole, selectedIssueId, qi, v)}
                        className={`w-fit px-2 py-1 text-xs rounded-lg font-semibold transition ${
                          responses[selectedRole]?.[selectedIssueId]?.[qi] === v
                            ? "bg-cyan-500 text-black"
                            : "bg-gray-800 text-gray-50 hover:bg-gray-700"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Score row */}
              <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-800">
                <span className="text-gray-100">
                  {lang === "hi" ? "स्कोर" : "Score"}:{" "}
                  <span className="text-white font-semibold">
                    {getIssueStats(selectedRole, currentIssue).total} /{" "}
                    {getIssueStats(selectedRole, currentIssue).max}
                  </span>
                </span>
                <span className="text-gray-100">
                  {lang === "hi" ? "सुसंगतता" : "Consistency"}:{" "}
                  {issueConsistencyIndex(selectedIssueId, selectedRole)}
                </span>
              </div>

              {/* Score band */}
              {(() => {
                const { total } = getIssueStats(selectedRole, currentIssue);
                const band = currentIssue.scoring?.bands?.find((b) => {
                  // FIX: defensive parse handles both "–" and "-"
                  const parts = b.range.split(/[–-]/).map((s) => Number(s.trim()));
                  return parts.length === 2 && total >= parts[0] && total <= parts[1];
                });
                if (!band) return null;
                return (
                  <div className={`rounded-xl p-4 border ${bandColor(band.range)}`}>
                    <p className="text-xs font-semibold mb-1 text-cyan-100 ">
                      {typeof band.label === "object" ? band.label[lang] : band.label}
                      <span className="text-gray-100 font-normal ml-1">
                        (range {band.range})
                      </span>
                    </p>
                    <p className="text-xs leading-relaxed text-gray-50">
                      {typeof band.insight === "object" ? band.insight[lang] : band.insight}
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Action bar */}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-800">
            {[
              { icon: "🤖", label: lang === "hi" ? "एआई जांच" : "Check with AI" },
              { icon: "💾", label: lang === "hi" ? "सहेजें"    : "Save"          },
              { icon: "📄", label: lang === "hi" ? "PDF"        : "Download PDF"  },
              { icon: "📤", label: lang === "hi" ? "साझा करें" : "Share"         },
              { icon: "👩‍⚕️",label: lang === "hi" ? "विशेषज्ञ" : "Expert"        },
              { icon: "🖨",  label: lang === "hi" ? "छापें"     : "Print"         },
            ].map((btn, i) => (
              <button
                key={i}
                disabled
                className="flex items-center justify-center gap-1 w-fit px-2 py-1 rounded-lg text-xs bg-teal-900 text-teal-300 opacity-40 cursor-not-allowed"
              >
                <span>{btn.icon}</span>
                <span className="truncate">{btn.label}</span>
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-gray-200">
            Actions unlock after login & full release.
          </p>
        </div>
      </div>
    );
  };

  export default GLOverallAssessment;